import { FC, ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'src/components/design-system/Button';
import Modal from 'src/components/Modal';
import { MESSAGE_COLORS } from 'src/constants/colors';
import { MESSAGE_MODE } from 'src/constants/messageMode';
import { MESSAGE_STATUS, MESSAGE_STATUS_CLASS, SPECIAL_SENDER } from 'src/constants/messageStatus';
import useDeviceType from 'src/hooks/useDeviceType';
import usePortrait from 'src/hooks/usePortrait';
import useWindowSize from 'src/hooks/useWindowSize';
import { Message } from 'src/services/messages_services/useGetMessageApi';
import { sanitizePlainText } from 'src/utils/sanitization';

export interface BaseMessageDetailsModalProps {
  visible: boolean;
  closeModal: () => void;
  message: Message | null;
  mode: typeof MESSAGE_MODE.INBOX | typeof MESSAGE_MODE.OUTBOX;
  title?: string | React.ReactElement<any>;
  currentStatus?: string;
  isContactInfoExpanded: boolean;
  renderActions?: () => ReactNode;
  renderContactInfo?: () => ReactNode;
  renderAdditionalContent?: () => ReactNode;
}

const BaseMessageDetailsModal: FC<BaseMessageDetailsModalProps> = ({
  visible,
  closeModal,
  message,
  mode,
  title,
  currentStatus,
  isContactInfoExpanded,
  renderActions,
  renderContactInfo,
  renderAdditionalContent,
}) => {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const deviceType = useDeviceType();
  const isPortrait = usePortrait();

  const modalWidth = useMemo(
    () =>
      deviceType !== 'desktop'
        ? isPortrait
          ? `${width}px`
          : `${Math.min(width, 750)}px`
        : '750px',
    [deviceType, isPortrait, width]
  );

  // Helper function to capitalize first letter of status
  const capitalizeFirstLetter = (str: string): string => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Consolidated status mapping to eliminate duplicate switch logic
  const STATUS_MAPPING: Record<string, { className: string; backgroundColor: string }> = {
    [MESSAGE_STATUS.NEW]: {
      className: MESSAGE_STATUS_CLASS.NEW,
      backgroundColor: MESSAGE_COLORS.STATUS_NEW,
    },
    [MESSAGE_STATUS.READ]: {
      className: MESSAGE_STATUS_CLASS.READ,
      backgroundColor: MESSAGE_COLORS.STATUS_READ,
    },
    [MESSAGE_STATUS.VIEWED]: {
      className: MESSAGE_STATUS_CLASS.VIEWED,
      backgroundColor: MESSAGE_COLORS.STATUS_VIEWED,
    },
    [MESSAGE_STATUS.REPLIED]: {
      className: MESSAGE_STATUS_CLASS.REPLIED,
      backgroundColor: MESSAGE_COLORS.STATUS_REPLIED,
    },
    [MESSAGE_STATUS.DELETED_BY_RECIPIENT]: {
      className: MESSAGE_STATUS_CLASS.DELETED_BY_RECIPIENT,
      backgroundColor: MESSAGE_COLORS.STATUS_DELBYRECIP,
    },
    [MESSAGE_STATUS.DELETED_BY_SENDER]: {
      className: MESSAGE_STATUS_CLASS.DELETED_BY_SENDER,
      backgroundColor: MESSAGE_COLORS.STATUS_DELBYSENDER,
    },
    [MESSAGE_STATUS.DELETED]: {
      className: MESSAGE_STATUS_CLASS.DELETED,
      backgroundColor: MESSAGE_COLORS.STATUS_DEL,
    },
    'deleted by all': {
      className: MESSAGE_STATUS_CLASS.DELETED,
      backgroundColor: MESSAGE_COLORS.STATUS_DEL,
    },
  };

  // Helper function to get CSS class for message status
  const getStatusClassName = (status: string): string => {
    const mapping = STATUS_MAPPING[status.toLowerCase()];
    return mapping?.className || MESSAGE_STATUS_CLASS.DEFAULT;
  };

  // Helper function to get status background color
  const getStatusBackgroundColor = useCallback((status: string): string => {
    const mapping = STATUS_MAPPING[status.toLowerCase()];
    return mapping?.backgroundColor || MESSAGE_COLORS.STATUS_READ;
  }, []);

  // Helper function to get priority color (matching grid display)
  const getPriorityColor = useCallback((priority: string): string => {
    const priorityLower = priority?.toLowerCase() || '';
    if (priorityLower === 'urgent') return 'firebrick';
    if (priorityLower === 'high') return '#e66b00';
    if (priorityLower === 'low') return '#1e90ff';
    return 'black'; // Normal or default
  }, []);

  // Select message ID text for manual copy (bypasses Clipboard API permissions)
  const selectMessageId = (e: React.MouseEvent<HTMLInputElement>) => {
    e.currentTarget.select();
    toast.info(t('Press Ctrl+C to copy'), {
      position: 'top-right',
    });
  };

  if (!message) return null;

  const lowercaseCreatedBy = message.createdByUserName.toLowerCase();
  const backgroundColor =
    lowercaseCreatedBy === SPECIAL_SENDER.SYSTEM_PROCESS ? '#E4EBF4' : undefined;

  return (
    <Modal
      visible={visible}
      closeModal={closeModal}
      title={
        title ||
        (mode === MESSAGE_MODE.INBOX
          ? String(t('Inbox Message Details'))
          : String(t('Outbox Message Details')))
      }
      width={modalWidth}
    >
      <div
        className={`min-h-[600px] max-h-[800px] ${
          isContactInfoExpanded ? 'overflow-y-auto' : 'overflow-hidden'
        }`}
        style={{
          height: 'auto',
          backgroundColor,
        }}
      >
        <div className="px-6 pt-3 pb-6 space-y-2">
          {/* Action Buttons (mode-specific) with Close button */}
          <div className="message-layout flex-center">
            {renderActions && renderActions()}
            <Button color="secondary" size="md" onClick={closeModal}>
              {t('Close')}
            </Button>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-blue-800 mb-1">{t('Status')}</label>
            <div
              className={getStatusClassName(currentStatus || message.decodedStatus)}
              style={{
                backgroundColor: getStatusBackgroundColor(currentStatus || message.decodedStatus),
                color: '#000',
                padding: '4px 12px',
                borderRadius: '4px',
                display: 'inline-block',
                fontSize: '0.875rem',
                fontWeight: '600',
              }}
            >
              {sanitizePlainText(
                capitalizeFirstLetter(t((currentStatus || message.decodedStatus).toLowerCase()))
              )}
            </div>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-blue-800 mb-1">{t('Priority')}</label>
            <div
              className="text-sm font-semibold"
              style={{
                color: getPriorityColor(
                  String(message.decodedPriority || message.priority || 'normal')
                ),
              }}
            >
              {message.decodedPriority || message.priority
                ? sanitizePlainText(t(String(message.decodedPriority || message.priority)))
                : t('N/A')}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-blue-800 mb-1">{t('Subject')}</label>
            <div className="text-gray-900 text-sm">{sanitizePlainText(message.subject)}</div>
          </div>

          {/* Date Sent/Received */}
          <div>
            <label className="block text-sm font-medium text-blue-800 mb-1">
              {mode === MESSAGE_MODE.INBOX ? t('Date Sent') : t('Sent On')}
            </label>
            <div className="text-gray-900 text-sm">
              {message.creationDate
                ? new Date(message.creationDate).toLocaleString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                  })
                : t('N/A')}
            </div>
          </div>

          {/* Contact Information (mode-specific rendering) */}
          {renderContactInfo && renderContactInfo()}

          {/* Message Content */}
          <div>
            <label className="block text-sm font-medium text-blue-800 mb-1">{t('Message')}</label>
            <div
              className={`${
                lowercaseCreatedBy === SPECIAL_SENDER.SYSTEM_PROCESS
                  ? 'bg-transparent'
                  : 'bg-gray-50'
              } p-4 rounded-md border min-h-[120px] max-h-[300px] overflow-y-auto text-gray-900 whitespace-pre-wrap text-sm`}
              style={{ maxWidth: '550px' }}
            >
              {sanitizePlainText(message.message.replace(/\\n/g, '\n'))}
            </div>
          </div>

          {/* Additional Content (mode-specific - e.g., attachments) */}
          {renderAdditionalContent && renderAdditionalContent()}

          {/* Message ID */}
          <div>
            <label className="block text-xs font-medium text-blue-800 mb-1">
              {t('Message ID')}
            </label>
            <input
              type="text"
              readOnly
              value={message.messageId}
              onClick={selectMessageId}
              className="text-gray-600 text-xs font-mono bg-gray-100 px-3 py-2 rounded cursor-pointer hover:bg-gray-200 transition-colors"
              title={t('Click to select, then press Ctrl+C to copy')}
              style={{ width: '200px' }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BaseMessageDetailsModal;
